@if (label) {
  <app-control-header
    [label]="label"
    [sublabel]="sublabel"
    [icon]="labelIcon"
    [labelParamsObj]="labelParamsObj"
    [index]="index"
    [required]="required"
    [labelWithColon]="labelWithColon"
    [descriptionTooltipText]="descriptionTooltipText"
    [infoTooltip]="infoTooltip"
    [infoTooltipPlacement]="infoTooltipPlacement"
  ></app-control-header>
}

<div class="flex gap-x-3 items-center h-10">
  <div
    class="wrapper"
    [class.hover:border-navy-90]="(isDisabled$ | async) === false"
    [class.is-with-addon]="addonText"
    [class.border-error]="errorsDisplayed"
  >
    <div class="h-full flex-1 relative">
      @if (placeholder) {
        <app-control-placeholder
          #placeholderElement
          class="absolute z-10 pointer-events-none"
          [class.hidden]="!isPlaceholderDisplayed"
          [placeholder]="placeholder"
          [placeholderParamsObj]="placeholderParamsObj"
          [placeholderIcon]="placeholderIcon"
        ></app-control-placeholder>
      }

      <div
        [ngbTooltip]="displayTextTooltip && textfield.offsetWidth < textfield.scrollWidth ? textfield.value : null"
        [placement]="'top bottom'"
        [tooltipClass]="'default-tooltip'"
      >
        @if (!isActive) {
          @if (getHighlightedSyntax(); as tokens) {
            <div
              data-test-id="highlight-container"
              class="text-backdrop flex items-center w-full h-full absolute top-0 left-0 z-0 pointer-events-none border-none overflow-hidden whitespace-nowrap"
            >
              @for (textPart of tokens; track $index) {
                @switch (textPart.type) {
                  @case ('term') {
                    <span data-test-id="highlighted" class="text-blue-70">{{ textPart.value }}</span>
                  }
                  @default {
                    <span>{{ textPart.value }}</span>
                  }
                }
              }
            </div>

            <!-- This div is used to compensate a bug with overflow that doesn't clip text and allows it to overflow
            This is only displayed if there are terms to highlight. -->
            <div class="min-h-10 min-w-10 absolute right-0 top-0 bg-white pointer-events-none"></div>
          }
        }

        @if (valuePrefix) {
          <span
            #prefixRef
            class="absolute whitespace-nowrap pointer-events-none inline-flex items-center h-10 text-base font-main text-navy-70"
          >
            {{ valuePrefix }}
          </span>
        }

        <input
          #textfield
          class="outline-none bg-white w-full border-none"
          id="{{ fieldName }}-textfield"
          [class.padding-right]="isClearButtonDisplayed"
          [class.opacity-0]="(!isActive && getHighlightedSyntax()) || isPlaceholderDisplayed"
          [attr.maxLength]="maxLength"
          [attr.min]="minValue"
          [attr.max]="maxValue"
          [attr.readOnly]="readonly"
          [attr.data-test-id]="'input'"
          [attr.type]="inputType"
          [attr.inputmode]="inputMode"
          [shouldOpenTooltip]="displayErrorsAsTooltip && errorsDisplayed && isActive"
          [ngModel]="value"
          [disabled]="isDisabled$ | async"
          [autocomplete]="suggestions?.length || disableAutocomplete ? 'off' : 'on'"
          [ngbTooltip]="displayErrorsAsTooltip && errorsDisplayed ? errorsTemplate : null"
          [tooltipClass]="'default-tooltip text-field-error-tooltip'"
          [placement]="'top'"
          (ngModelChange)="modelChange($event)"
          (change)="modelChange(textfield.value)"
          (input)="input.emit($event)"
          (blur)="inputBlur()"
          (focusin)="handleInputFocusIn()"
          (focusout)="handleInputFocusOut()"
          (keydown)="keyDown.emit($event)"
        />
      </div>
    </div>

    @if (isClearButtonDisplayed) {
      <div class="clear-btn-wrapper flex flex-row items-center absolute h-full">
        <app-clear-button (click)="clear()"></app-clear-button>
      </div>
    }

    @if (addonText) {
      <div class="addon-text flex flex-row items-center bg-navy-30 whitespace-nowrap">
        {{ addonText | translate }}
      </div>
    }

    <div class="buttons flex items-center h-full mr-2 absolute right-0">
      <ng-content select="app-button"></ng-content>

      <ng-content select="button"></ng-content>

      @if (showHideText && textfieldElement) {
        <button class="mulish white toggle-show-hide-password" (click)="toggleShowHide()">
          <svg-icon [src]="textfieldElement.nativeElement.type === 'password' ? 'show_n-a' : 'hide_n-a'"></svg-icon>
        </button>
      }
    </div>
  </div>

  @if (removable) {
    <app-icon-button
      class="self-end"
      [type]="'basic'"
      [svgIconPath]="'trash-bucket'"
      (click)="removeControl.emit(fieldName)"
    ></app-icon-button>
  }
</div>

@if (textfieldElement$ | async; as txtField) {
  @if (txtField && suggestions?.length) {
    <app-autocomplete
      [autocompleteData]="suggestions"
      [maxItemsToDisplay]="suggestionsConfig?.maxItemsToDisplay"
      [inputElement]="txtField"
      (select)="value = $event"
    ></app-autocomplete>
  }
}

@if (errorsDisplayed || displayCharactersCounter) {
  <div
    class="flex justify-end items-center w-full"
    [class.relative]="isCharactersCounterAbsolutePositioned || isControlErrorAbsolutePositioned"
  >
    @if (!displayErrorsAsTooltip && errorsDisplayed) {
      <ng-container *ngTemplateOutlet="errorsTemplate"></ng-container>
    }

    @if (displayCharactersCounter) {
      <app-characters-counter
        class="top-0 mt-1"
        [class.absolute]="isCharactersCounterAbsolutePositioned"
        [value]="value"
        [maxLength]="maxLength"
      ></app-characters-counter>
    }
  </div>
}

<ng-template #requiredPopover>
  <div class="popover-container">
    <span translate>core.formControls.requiredTooltip</span>
  </div>
</ng-template>

<ng-template #errorsTemplate>
  <app-control-errors
    class="flex-1 mt-1"
    [errorTexts]="errorTexts"
    [ngClass]="{
      'absolute top-0 left-0': isControlErrorAbsolutePositioned,
    }"
  ></app-control-errors>
</ng-template>
